"use client"

import { Spinner, Trash } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import type { CartPendingAction, CartDrawerContextValue } from "../../context/cart-drawer-context"
import { formatCartDrawerMoney } from "../../format-money"
import type { CartDrawerLine as CartDrawerLineType } from "../../types"
import CartQuantityControl from "../cart-quantity-control"
import styles from "./cart-drawer-line.module.css"

export default function CartDrawerLine({
  line,
  currencyCode,
  pending,
  locked = false,
  mutationError,
  setLineQuantity,
  closeCart,
}: {
  line: CartDrawerLineType
  currencyCode: string
  pending: CartPendingAction | null
  locked?: boolean
  mutationError?: string | null
  setLineQuantity: CartDrawerContextValue["setLineQuantity"]
  closeCart: () => void
}) {
  const controlsLocked = locked || Boolean(pending)
  const activeAction = pending?.lineKey === line.key ? pending.action : null
  const activePending = Boolean(activeAction)
  const money = (amount: number) => formatCartDrawerMoney(amount, currencyCode)
  const href = line.handle ? `/products/${line.handle}` : null
  const visibleOptions = line.options.filter(
    (option) => !(option.name === "Title" && option.value === "Default Title")
  )
  const thumbnail = <Thumbnail thumbnail={line.thumbnail} size="square" />

  return (
    <li
      className={styles.line}
      data-testid={`cart-drawer-line-${line.key}`}
      data-pending={activePending ? "true" : undefined}
      aria-busy={activePending ? "true" : undefined}
    >
      <div className={styles.grid}>
        <div className={styles.image} data-testid="cart-line-image">
          {href ? (
            <LocalizedClientLink
              href={href}
              className={styles.imageLink}
              onClick={closeCart}
              tabIndex={-1}
              aria-hidden="true"
            >
              {thumbnail}
            </LocalizedClientLink>
          ) : thumbnail}
        </div>

        <div className={styles.content}>
          <div className={styles.headingRow}>
            <div className={styles.meta}>
              {href ? (
                <LocalizedClientLink href={href} className={styles.titleLink} onClick={closeCart} aria-label={line.title}>
                  <p className={styles.title}>{line.title}</p>
                </LocalizedClientLink>
              ) : <p className={styles.title}>{line.title}</p>}

              {visibleOptions.length > 0 ? (
                <ul className={styles.options}>
                  {visibleOptions.map((option) => (
                    <li key={`${option.name}:${option.value}`}><small>{option.name}: <span>{option.value}</span></small></li>
                  ))}
                </ul>
              ) : null}

              {line.promotionLabels.map((label) => <span key={label} className={styles.bundleLabel}>{label}</span>)}
            </div>

            <div className={styles.prices} data-testid="cart-line-prices">
              <strong className={styles.effective} data-parity-exception="line-allocation-value">{money(line.effectiveTotal)}</strong>
              {line.compareAtTotal > line.effectiveTotal ? (
                <span className={styles.compareAt} data-parity-exception="line-allocation-value">{money(line.compareAtTotal)}</span>
              ) : null}
            </div>
          </div>

          {line.savings > 0 ? <span className={styles.savings}>{money(line.savings)} tasarruf</span> : null}

          <div className={styles.bottomRow}>
            <div className={styles.actions}>
              <CartQuantityControl
                value={line.quantity}
                max={line.maxQuantity}
                disabled={controlsLocked}
                pendingAction={activeAction}
                onChange={(quantity, action) => void setLineQuantity(line, quantity, action)}
              />
              <button
                type="button"
                className={styles.remove}
                aria-label={`${line.title} ürününü sepetten kaldır`}
                disabled={controlsLocked}
                onClick={() => void setLineQuantity(line, 0, "remove")}
              >
                {activeAction === "remove" ? <Spinner /> : <Trash />}
              </button>
            </div>
          </div>

          {mutationError ? <p className={styles.rowError} role="alert">{mutationError}</p> : null}
        </div>
      </div>
    </li>
  )
}
